/**
 * Simple typed wrapper for the localstorage setItem api
 *
 * NOTE: Resilient helpers with quota handling & eviction strategies are documented in
 * `LOCAL_STORAGE_CHANGES.md` (see project root). This file now contains both the
 * original thin wrappers and new safe variants (`safeSetLocalStorageItem`, etc.).
 *
 * @param key {string} key to store the value at in local storage
 * @param value {any} value to be stored in local storage
 */
export function setLocalStorageItem(key: string, value: any): void {
  localStorage.setItem(key, JSON.stringify({ value }));
}

export function getLocalStorageItem<T>(key: string): T;
export function getLocalStorageItem<T>(key: string, otherwise: T): T;
/**
 * Simple typed wrapper for the localstorage getItem api
 *
 * @template T
 * @param key {string} key to store the value at in local storage
 * @param otherwise {T} optional default value to get back if the local storage value is undefined
 * @returns {T} value form the key-value pair in local storage or the default value
 */
export function getLocalStorageItem<T>(key: string, otherwise?: T): T | null {
  const data: string | null = localStorage.getItem(key);

  if (data) {
    return JSON.parse(data).value;
  }
  if (otherwise) {
    return otherwise;
  }
  return null;
}

/**
 *
 * @param key
 */
export function removeLocalStorageItem(key: string): void {
  localStorage.removeItem(key);
}

// ---- Safe / Resilient storage helpers ----
import { Logger } from './Logger';

interface SafeSetOptions {
  maxAttempts?: number; // Total attempts including initial
  // Function to extract sortable timestamp from a record (ISO or ms) used for oldest-first eviction
  timestampExtractor?: (record: any) => number;
  // Path to array inside object (e.g. 'records') or root if value is array
  arrayPath?: string;
  removeOnFail?: boolean; // Remove key on final failure
  // When true, set a flag key so UI can prompt user to clear site data
  setWarningFlagKey?: string;
}

/**
 * Safely write a JSON value to localStorage with QuotaExceededError recovery.
 * Default trimming: progressively slice arrays or .records arrays (75%, then 50%).
 */
export function safeSetLocalStorageItem(key: string, value: any, opts: SafeSetOptions = {}): boolean {
  const {
    maxAttempts = 3,
    timestampExtractor,
    arrayPath,
    removeOnFail = true,
    setWarningFlagKey,
  } = opts;

  let current = value;

  const evictOldestOne = (val: any): any => {
    if (!val) return val;
    let arr: any[] | undefined;
    if (Array.isArray(val)) {
      arr = val;
    } else if (arrayPath && Array.isArray((val as any)[arrayPath])) {
      arr = (val as any)[arrayPath];
    }
    if (!arr || arr.length <= 1) return val; // nothing to evict
    // Build sortable list
    const withTs = arr.map((r, i) => ({ i, ts: timestampExtractor ? timestampExtractor(r) : (Date.parse(r?.startTime) || 0) }));
    withTs.sort((a, b) => a.ts - b.ts); // ascending => oldest first
    const oldestIndex = withTs[0].i;
    const newArr = arr.filter((_, idx) => idx !== oldestIndex);
    if (Array.isArray(val)) return newArr;
    return { ...(val as any), [arrayPath!]: newArr };
  };

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      localStorage.setItem(key, JSON.stringify({ value: current }));
      Logger.info(`[SafeStorage] Stored key='${key}' attempt=${attempt}`);
      return true;
    } catch (e: any) {
      if (e && (e.name === 'QuotaExceededError' || e.code === 22)) {
        Logger.info(`[SafeStorage] QuotaExceededError key='${key}' attempt=${attempt}`);
        if (attempt < maxAttempts) {
          const beforeLength = Array.isArray(current) ? current.length : (arrayPath && Array.isArray(current?.[arrayPath]) ? current[arrayPath].length : undefined);
          current = evictOldestOne(current);
          const afterLength = Array.isArray(current) ? current.length : (arrayPath && Array.isArray(current?.[arrayPath]) ? current[arrayPath].length : undefined);
          Logger.info(`[SafeStorage] Evicted oldest record key='${key}' length ${beforeLength} -> ${afterLength}`);
          continue;
        }
        if (removeOnFail) {
          try { localStorage.removeItem(key); } catch {}
        }
        if (setWarningFlagKey) {
          try { localStorage.setItem(setWarningFlagKey, JSON.stringify({ value: true })); } catch {}
        }
        Logger.error(`[SafeStorage] Give up storing key='${key}' after ${attempt} attempts.`);
        return false;
      }
      Logger.error(`[SafeStorage] Unexpected error writing key='${key}': ${String(e)}`);
      return false;
    }
  }
  return false;
}

export function safeGetLocalStorageItem<T>(key: string, otherwise?: T): T | null {
  try { return getLocalStorageItem<T>(key, otherwise as any); } catch { return otherwise ?? null; }
}
